import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { push } from "react-router-redirect";
import {
  displayActionableToast,
  displayToast,
} from "../../components/app/AppToast";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { useFirebaseUser } from "../../domains/user/hooks/useFirebaseUser";
import { apiClient, useApiQuery, type Api } from "../../services/api";
import {
  gaEventVisualAbstractCtaShown,
  gaEventVisualAbstractRequested,
  gaEventVisualAbstractUnavailable,
} from "../../tools/analytics/domains/article.analytics";
import { watchVisualAbstract } from "./visualAbstractWatchStore";

export type VisualAbstractList =
  Api["get"]["/webapp/private/articles/:id/visual-abstracts"]["output"]["body"];

/**
 * The visual abstracts of one paper, as the article page offers them: the
 * phone's composer pill and the desktop header button. Both read the same list
 * and request a poster the same way; each decides which states it shows (the
 * desktop button also opens a ready poster and offers a new one after a
 * failure). The impression counts only the offer both show, a paper with no
 * poster yet.
 *
 * `list` is null until the API has settled the paper's eligibility, and for a
 * signed-out reader: the endpoint is private and the article page is reachable
 * logged out, so asking anyway would 401 on every paper those readers open.
 */
export function useArticleVisualAbstracts({
  articleId,
  articleTitle,
  roomId,
}: {
  articleId: string;
  /** Rides into the watch entry, so the completion toast names this paper. */
  articleTitle: string;
  roomId: string | null;
}): { list: VisualAbstractList | null; request: () => Promise<void> } {
  const { t } = useTranslation();
  const { isAuthenticatedFirebase } = useFirebaseUser();

  const refetchInterval = useCallback(
    (
      data:
        | Api["get"]["/webapp/private/articles/:id/visual-abstracts"]["output"]
        | undefined
    ) =>
      data !== undefined &&
      data.body.artifacts.some(
        (artifact) =>
          artifact.status === "queued" || artifact.status === "generating"
      )
        ? 3_000
        : false,
    []
  );

  // The key is the one the conversation's card and the header sheet already
  // use, so a run started here is reflected in both without a second request.
  const listQuery = useApiQuery(
    "/webapp/private/articles/:id/visual-abstracts",
    isAuthenticatedFirebase
      ? { param: { id: articleId }, query: { roomId }, refetchInterval }
      : false
  );
  const list = listQuery.state === "success" ? listQuery.body : null;

  // Only the offer is an impression: an eligible paper with no poster yet. The
  // generating state that follows the reader's own tap is not something they
  // were shown and could have taken, and counting it would deflate this
  // surface's conversion with its own successes.
  const isOffered =
    list !== null &&
    list.eligibility.state === "eligible" &&
    list.artifacts.length === 0;
  useEffectOnce(() => {
    if (!isOffered) return;
    gaEventVisualAbstractCtaShown({
      articleId,
      roomId,
      affordance: "generate",
      trigger: "article_page",
    });
  }, [articleId, roomId, isOffered]);

  // An unsettled query and a REFUSED paper both offer nothing, and only the
  // second is a fact about the feature's reach.
  const ineligibilityReason =
    list !== null && list.eligibility.state === "ineligible"
      ? list.eligibility.reason
      : null;
  useEffectOnce(() => {
    if (ineligibilityReason === null) return;
    gaEventVisualAbstractUnavailable({
      articleId,
      roomId,
      reason: ineligibilityReason,
    });
  }, [articleId, roomId, ineligibilityReason]);

  async function request(): Promise<void> {
    try {
      const response = await apiClient.post(
        "/webapp/private/articles/:id/visual-abstracts",
        { param: { id: articleId }, json: { roomId } }
      );
      // Registered from the 202 rather than from the refetch below: the reader
      // is free to leave the page the moment they tap, and a run nobody is
      // watching lands with no toast.
      watchVisualAbstract({ id: response.body.id, articleId, articleTitle });
      gaEventVisualAbstractRequested({
        articleId,
        roomId,
        sourceType: response.body.sourceType,
        trigger: "article_page",
      });
      await listQuery.refetch();
      displayActionableToast(
        {
          title: t("Visual abstract generation started"),
          description: t("Follow its progress in Visual abstracts"),
        },
        "success",
        () => push("/visual-abstracts")
      );
    } catch {
      // The cause is already captured by the api client's onRequestError; the
      // reader gets one stable sentence rather than a server string. The 422
      // this can carry — eligibility changed between the GET that offered the
      // poster and this POST — is a race the reader cannot act on anyway.
      displayToast(t("Unable to generate the visual abstract"), "error");
    }
  }

  return { list, request };
}
